
import { computed, defineComponent, reactive, ref, watch } from 'vue'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import MixSelectors
  from '@/views/dashboard/_components/group-policy-settings/_components/mix-selectors/mix-selectors.vue'
import { ElMessage } from 'element-plus'
import ContentAdaptLayout from '@/views/dashboard/_components/content-adapt-layout/content-adapt-layout.vue'
import TaskTimeEdit from '@/views/dashboard/_components/task-time/task-time-edit.vue'
import PolicyBindScope from '@/views/dashboard/policy/_components/policy-bind-scope.vue'
import { editToList, PolicyCommonService } from '@/views/dashboard/policy/policy-common.service'
import {
  DEFAULT_SETTING,
  SettingsRes
} from '@/views/dashboard/policy/process-control-edit/process-control-edit.service'
import { PermissionFe } from '@/services/service-permission.service'
import { PolicySettingType, PolicyType } from '@/views/dashboard/_enum/policy-type'
import ProcessEdit from '@/views/dashboard/_components/process-edit/process-edit.vue'
import { ProcessInfos } from '@/views/dashboard/policy/process-control/process-control.service'
import { isObjectFieldEmpty } from '@/utils/tool'

const validateProcess = (rule: any, value: ProcessInfos[], callback: any) => {
  const processList = value.filter(v => !isObjectFieldEmpty(v))
  const isExistEmpty = processList.find((item: ProcessInfos) => {
    return !item.name
  })
  const isMd5Error = processList.find((item: ProcessInfos) => {
    return item.md5 && item.md5.length !== 32
  })
  if (!processList.length) {
    callback(new Error('请设置控制目标进程'))
  } else if (isExistEmpty) {
    callback(new Error('进程名不能为空'))
  } else if (isMd5Error) {
    callback(new Error('请填写32位MD5'))
  } else {
    callback()
  }
}

export default defineComponent({
  components: {
    ProcessEdit,
    PolicyBindScope,
    TaskTimeEdit,
    MixSelectors,
    ContentAdaptLayout
  },
  props: {
    drawerMode: { type: Boolean, default: false },
    initialPolicyId: { type: String, default: '' },
    initialFromGroupId: { type: String, default: undefined },
    initialReadonly: { type: Boolean, default: false },
    /** 独立页由路由 props 注入 */
    policyId: { type: String, default: '' },
    fromGroupId: { type: String, default: undefined },
    readonly: { type: Boolean, default: false },
  },
  emits: ['close', 'success'],
  setup (props, context) {
    const injector = useReflectiveInjector([PolicyCommonService])
    const policyCommonService = injector.get(PolicyCommonService)

    const isLoading = ref<boolean>(false)
    const resolvedPolicyId = computed(() =>
      props.drawerMode ? (props.initialPolicyId || '') : (props.policyId || '')
    )
    const resolvedFromGroupId = computed(() =>
      props.drawerMode ? (props.initialFromGroupId || undefined) : (props.fromGroupId || undefined)
    )
    const isReadonly = computed(() =>
      props.drawerMode ? props.initialReadonly : props.readonly
    )
    const strategyData = ref({
      strategy: JSON.parse(JSON.stringify(DEFAULT_SETTING)),
      bind_mode: 0,
      device_ids: [] as string[],
      strategy_group_ids: [] as string[],
    } as SettingsRes)
    const formNode = ref()
    const groupEditStrategyOverComputed = computed(() => ({
      type: PolicyType.processControl,
      current: resolvedPolicyId.value,
    }))
    const formRules = reactive({
      name: [
        {
          required: true,
          message: '请输入策略名称',
          trigger: 'blur',
        },
      ],
      'strategy.process_infos': [
        {
          required: true,
          message: '请设置控制目标进程',
          trigger: 'change',
        },
        {
          validator: validateProcess,
          trigger: 'change',
        }
      ],
      strategy_group_ids: [
        {
          validator: (rule: any, value: any, callback: (e?: Error) => void) => {
            const mode = strategyData.value.bind_mode ?? 0
            if (mode === 0 && (!value || !value.length)) {
              callback(new Error('请选择应用分组'))
            } else {
              callback()
            }
          },
          trigger: 'change',
        },
      ],
      device_ids: [
        {
          validator: (rule: any, value: any, callback: (e?: Error) => void) => {
            const mode = strategyData.value.bind_mode ?? 0
            if (mode === 1 && (!value || !value.length)) {
              callback(new Error('请选择终端'))
            } else {
              callback()
            }
          },
          trigger: 'change',
        },
      ],
    })

    function getSettings () {
      isLoading.value = true
      policyCommonService.getSettings({
        id: resolvedPolicyId.value,
        type: PolicySettingType.processControl,
      }).then(response => {
        strategyData.value = response
        const process = strategyData.value.strategy.process_infos
        strategyData.value.strategy.process_infos = process?.length ? process : [{}]
      }).finally(() => {
        isLoading.value = false
      })
    }

    async function saveSetting () {
      const strategy = {
        ...strategyData.value.strategy,
        process_infos: strategyData.value.strategy.process_infos.filter(v => !isObjectFieldEmpty(v))
      }

      if (!await checkBeforeSubmit()) {
        return
      }
      policyCommonService.saveSettings({
        id: resolvedPolicyId.value,
        strategy: JSON.stringify(strategy),
        name: strategyData.value.name,
        group_ids: strategyData.value.strategy_group_ids ?? [],
        device_ids: strategyData.value.device_ids,
        bind_mode: strategyData.value.bind_mode ?? 0,
        type: PolicySettingType.processControl,
        strategy_type: PolicyType.processControl,
      }).then(() => {
        ElMessage.success('保存成功')
        if (props.drawerMode) {
          context.emit('success')
        } else {
          goListPage()
        }
      })
    }

    async function checkBeforeSubmit () {
      if (!await formNode.value.validate().catch(() => false)) {
        ElMessage.error('表单填写错误，请检查')
        return false
      }
      return true
    }

    function cancel () {
      if (props.drawerMode) {
        context.emit('close')
      } else {
        goListPage()
      }
    }

    function goListPage () {
      editToList({ path: '/dashboard/process-control', fromGroupId: resolvedFromGroupId.value })
    }

    watch(
      resolvedPolicyId,
      (id) => {
        if (id) {
          getSettings()
        }
      },
      { immediate: true }
    )

    return {
      DEFAULT_SETTING,
      PermissionFe,
      groupEditStrategyOverComputed,
      isLoading,
      resolvedFromGroupId,
      resolvedPolicyId,
      isReadonly,
      strategyData,
      formNode,
      formRules,
      saveSetting,
      cancel,
    }
  }
})
